import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

// 2026-08-14: re-pulled the actual Figma master (fileKey HJevYB4nKzCBzVhbCuMpmI,
// node 12:539 "Badges") node-by-node via get_design_context — not the written
// codex — after the shape fix above turned out to contradict a "confirmed
// divergence" note that nobody had re-checked against the real component.
// Two more real gaps found the same way:
//   - the `default`/neutral variant was outline-only (no fill) per codex §9.6;
//     the master's neutral badge (Pill AND Badge color) fills
//     bg-neutral-50/border-neutral-200/text-neutral-700 (#fafafa/#e5e5e5/#404040
//     — exactly `--surface`/`--border-subtle`/`--foreground-secondary`). The
//     §9.6 "no loud filled pills" reasoning was about the ACCENT variants
//     (already correctly de-saturated below); it never applied to neutral.
//   - `sm` was h-5 (20px) fixed; the master's own Height column is 22px
//     (2px padding-y × 2 + 18px line-height), confirmed by re-deriving the
//     geometry from the pulled node, not just the height number.
//
// Size axis + accent border/text step per untitledui-reference.md "Badge"
// (frame `1046:3819`, DES-53): the master's 3-step colour ramp is `-50` fill /
// `-200` border / `-700` text. We hold only one token per hue (`--info`
// etc.), so the `-700` text step is that base token (already the darkest step
// in use) and the `-200` border step is the same token at low alpha
// (`border-info/20`) — the same alpha-tint convention already used for
// status surfaces elsewhere (scan-status-indicator.tsx, brand-tag.tsx). No new
// token needed. Size geometry (px-2/2.5/3) matches the master's Pill pad
// column exactly on our 4px scale; sm/md font sizes come from the master's
// Font column (12px / 14px).
const badgeVariants = cva(
  'group/badge inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden border border-transparent font-medium whitespace-nowrap transition-all focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none',
  {
    variants: {
      variant: {
        // 2026-08-22 (brand-token-migration T5 correction): re-checked against
        // the literal "Badges & status chips" markup in the Claude Design
        // source (Brand System.dc.html) — the actual ground truth this whole
        // migration is sourced from, superseding the UntitledUI-master note
        // above for border/shadow. Every accent chip there (positive/negative/
        // lime "Growing") is a flat *-soft fill with ZERO border; the only
        // bordered chip in the source is the neutral "outline"/unfilled one
        // (`background: var(--card); border: 1px solid var(--border-strong)`,
        // "Not yet visible") — no chip anywhere carries a shadow.
        //
        // Neutral default now maps to THAT bordered chip, not the old
        // surface/border-subtle pairing: --card (#ffffff) and --border-strong
        // (color-mix(ink-900 14%)) are the literal tokens the source names,
        // and --border-strong's own value was already derived from this same
        // design source's formula (see the --border-strong comment above) —
        // --border-subtle/--surface were the old UntitledUI-era mapping and
        // read visibly lower-contrast than the source's spec.
        // round-4 audit, corrected in review: the source's literal neutral-chip
        // color is var(--ink-400), materially lighter than --foreground-secondary's
        // old #404040 — but --ink-400 is theme-invariant (no .dark override
        // exists), where --foreground-secondary carries a real dark-adapted
        // value. Reverted to --foreground-secondary: matching the light-mode
        // spec exactly is not worth a WCAG contrast failure in dark mode. Same
        // "recorded gap" shape as --amber's own missing dark value.
        default: 'border-border-strong bg-card text-foreground-secondary [a]:hover:bg-muted',
        secondary: 'bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80',
        // destructive already had no border class (only the inherited
        // base `border-transparent`), so it already matches the accent-chip
        // pattern below (flat *-soft-equivalent fill, no border) — no change.
        destructive:
          'bg-destructive/10 text-destructive focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:focus-visible:ring-destructive/40 [a]:hover:bg-destructive/20',
        // outline's own name declares the bordered/unfilled intent the source
        // reserves for the neutral chip — kept bordered, unlike the accent
        // variants below.
        outline: 'border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground',
        ghost: 'hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50',
        // round-4 audit: same --primary(lime-200)-as-text fix as Button's
        // `link` variant — the design doc's own prose names info tone for
        // inline links.
        link: 'text-info underline-offset-4 hover:underline',
        // Accent variants — flat *-bg fill, no border, no shadow, per the
        // source's "Leading"/"Growing"/"Low visibility" chip markup (2026-08-22
        // correction — these previously carried a border-{color}/20 hairline
        // that the design source does not have on any accent chip).
        // round-4 audit: text uses the dedicated *-text step (e.g.
        // --positive-text), not the vivid dot/border color (--positive) — the
        // source's own chip markup names a distinct, darker text step for
        // text-on-soft-fill ("Leading" = var(--positive-text), etc).
        indigo: 'bg-info-soft text-indigo-text',
        emerald: 'bg-emerald-bg text-emerald-text',
        rose: 'bg-rose-bg text-rose-text',
        amber: 'bg-amber-bg text-amber-text',
        // Featured/recommended/selected emphasis (brand-token-migration
        // 2026-08-22 follow-up) — never brand "you" semantics (that's
        // --series-1) and never info/link semantics (that's --info). Same
        // flat-fill, no-border treatment as the other accent variants above.
        primary: 'bg-primary-100 text-primary-900',
      },
      size: {
        // h-[22px]: the master's real Height column for sm — not on our 4px
        // scale (h-5=20/h-6=24 straddle it), and not re-derivable from
        // padding+leading without a second bracket value, so this is DES-13's
        // "genuinely one-off, no role to name" exception, same as
        // metric-bar.tsx's py-[11px].
        sm: 'h-[22px] px-2 py-0.5 text-xs [&>svg]:size-3!',
        md: 'h-6 px-2.5 py-0.5 text-sm [&>svg]:size-3.5!',
        lg: 'h-7 px-3 py-1 text-sm [&>svg]:size-3.5!',
      },
      // untitledui-reference.md "Badge": the master has two distinct types —
      // `Pill color` (full radius, our long-standing default) and `Badge color`
      // (6-8px radius). `rect` opts a call site into the latter; every existing
      // consumer keeps `pill` (zero-diff default).
      shape: {
        pill: 'rounded-full',
        // untitledui-reference.md: master's `sm`/`md` Badge-color radius is 6px
        // (`lg` is 8px) — `rounded-sm` resolves to `--radius-sm` (6px), not
        // `rounded-md` (8px, this app's button/input radius, too round at the
        // badge's 20-24px height).
        rect: 'rounded-sm',
      },
    },
    // The master's "Badge color" (rect) type also carries narrower horizontal
    // padding than "Pill color" at every size (sm 6 vs 8, md 8 vs 10, lg 10 vs
    // 12) — confirmed against the pulled node (Badge color/sm/Gray: px-[6px]
    // vs Pill color/sm/Gray: px-[8px]). All three land on registered Tailwind
    // steps (px-1.5/2/2.5), no bracket values needed; twMerge (via cn()) at
    // the call site resolves the px-* conflict against the size variant's own
    // padding in favor of whichever comes later in the concatenated string —
    // compoundVariants run after `variants`, so rect always wins.
    compoundVariants: [
      { shape: 'rect', size: 'sm', class: 'px-1.5' },
      { shape: 'rect', size: 'md', class: 'px-2' },
      { shape: 'rect', size: 'lg', class: 'px-2.5' },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'sm',
      shape: 'pill',
    },
  }
)

function Badge({
  className,
  variant = 'default',
  size = 'sm',
  shape = 'pill',
  render,
  ...props
}: useRender.ComponentProps<'span'> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: 'span',
    props: mergeProps<'span'>(
      {
        className: cn(badgeVariants({ variant, size, shape }), className),
      },
      props
    ),
    render,
    state: {
      slot: 'badge',
      variant,
      size,
      shape,
    },
  })
}

export { Badge, badgeVariants }
